import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, RefreshCw, ShieldCheck, Users } from "lucide-react";
import { personaImports } from "@/api/personaImports";
import { usePendingNavigationLock } from "./useDirtyNavigationGuard";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function dateLabel(value) {
  if (!value) return "Not yet";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  }).format(date);
}

function byteLabel(value) {
  if (!Number.isFinite(value)) return "Unknown";
  if (value < 1024) return `${value} B`;
  return `${(value / 1024).toFixed(value < 10240 ? 1 : 0)} KB`;
}

function StatusBadge({ status }) {
  const styles = {
    active: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
    pending: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
    failed: "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200",
    withdrawn: "bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-100",
  };
  const label = status ? status[0].toUpperCase() + status.slice(1) : "Available";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${styles[status] || "bg-teal-100 text-teal-900 dark:bg-teal-950 dark:text-teal-200"}`}>{label}</span>;
}

export default function PersonaImports() {
  const [data, setData] = useState({ exports: [], imports: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const [consent, setConsent] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const generation = useRef(0);
  const actionGeneration = useRef(0);
  const controllerRef = useRef(null);
  const actionControllerRef = useRef(null);
  const { query, update, reset } = useAdminListQuery();
  const [total, setTotal] = useState(0);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [historyError, setHistoryError] = useState("");
  usePendingNavigationLock(Boolean(busy));

  const load = useCallback(async () => {
    const target = ++generation.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const utc = (value, label) => {
        if (!value) return undefined;
        const date = new Date(`${value}Z`);
        if (!Number.isFinite(date.getTime())) throw new Error(`Invalid ${label} date`);
        return date.toISOString();
      };
      const historyFrom = utc(query.history_from, "history from");
      const historyTo = utc(query.history_to, "history to");
      const [feedResult, historyResult] = await Promise.allSettled([
        personaImports.list({ signal: controller.signal, query: { paged: "true", limit: "25", ...(query.q ? { q: query.q } : {}), ...(query.status ? { status: query.status } : {}), ...(query.offset ? { offset: query.offset } : {}) } }),
        personaImports.history({ signal: controller.signal, query: { paged: "true", limit: "25", ...(query.history_q ? { q: query.history_q } : {}), ...(query.history_status ? { status: query.history_status } : {}), ...(historyFrom ? { from: historyFrom } : {}), ...(historyTo ? { to: historyTo } : {}), ...(query.history_offset ? { offset: query.history_offset } : {}) } }),
      ]);
      if (generation.current !== target) return;
      if (feedResult.status === "fulfilled") {
        const result = feedResult.value;
        setData({ exports: result?.items || result?.exports || [], imports: [] });
        setTotal(Number(result?.total) || Number(result?.exports?.length) || 0);
      } else if (feedResult.reason?.name !== "AbortError") setError(feedResult.reason?.message || "Could not load the DI Voices feed.");
      if (historyResult.status === "fulfilled") {
        setHistory({ items: historyResult.value?.items || [], total: Number(historyResult.value?.total) || 0 });
        setHistoryError("");
      } else if (historyResult.reason?.name !== "AbortError") setHistoryError(historyResult.reason?.message || "Could not load import history.");
    } catch (loadError) {
      if (generation.current === target && loadError?.name !== "AbortError") setHistoryError(loadError?.message || "Could not load import history.");
    } finally {
      if (generation.current === target) setLoading(false);
    }
  }, [query.q, query.status, query.offset, query.history_q, query.history_status, query.history_from, query.history_to, query.history_offset]);

  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
      actionGeneration.current += 1;
      controllerRef.current?.abort();
      actionControllerRef.current?.abort();
    };
  }, [load]);

  const importsById = useMemo(() => new Map(data.imports.map((row) => [row.export_id, row])), [data.imports]);

  function openImport(item) {
    setSelected(item);
    setConsent(false);
    setError("");
    setActionError("");
    setNotice("");
  }

  async function submitImport(event) {
    event.preventDefault();
    if (!selected || !consent || busy) return;
    const exportId = selected.id;
    const target = ++actionGeneration.current;
    actionControllerRef.current?.abort();
    const controller = new AbortController();
    actionControllerRef.current = controller;
    setBusy(`import:${exportId}`);
    setActionError("");
    setNotice("");
    try {
      const result = await personaImports.importExport(exportId, { signal: controller.signal });
      if (actionGeneration.current !== target) return;
      setSelected(null);
      setConsent(false);
      setNotice(`${result?.persona_ids?.length || 0} persona${result?.persona_ids?.length === 1 ? "" : "s"} imported from DI Voices.`);
      await load();
    } catch (actionError) {
      if (actionGeneration.current === target && actionError?.name !== "AbortError") setActionError(actionError?.message || "The export could not be imported.");
    } finally {
      if (actionGeneration.current === target) setBusy("");
    }
  }

  async function reconcile() {
    if (busy) return;
    const target = ++actionGeneration.current;
    actionControllerRef.current?.abort();
    const controller = new AbortController();
    actionControllerRef.current = controller;
    setBusy("reconcile");
    setActionError("");
    setNotice("");
    try {
      const result = await personaImports.reconcile({ signal: controller.signal });
      if (actionGeneration.current !== target) return;
      const count = result?.withdrawn?.length || 0;
      setReconcileOpen(false);
      setNotice(count ? `${count} missing export${count === 1 ? " was" : "s were"} withdrawn.` : "Reconciliation complete. No imports were withdrawn.");
      await load();
    } catch (actionError) {
      if (actionGeneration.current === target && actionError?.name !== "AbortError") setActionError(actionError?.message || "The feed could not be reconciled.");
    } finally {
      if (actionGeneration.current === target) setBusy("");
    }
  }

  return (
    <section className="persona-imports mx-auto max-w-6xl text-foreground" aria-label="DI Voices persona imports" aria-busy={loading || Boolean(busy)}>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700 dark:text-teal-300">Persona sources</p>
          <h1 className="mt-1 text-3xl font-bold">DI Voices imports</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">Review the configured DI Voices export feed, confirm recorded participant consent, and create evidence-grounded personas from complete exports.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => void load()} disabled={loading || Boolean(busy)} className="persona-import-refresh inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-60"><RefreshCw className="h-4 w-4" aria-hidden="true" />Refresh feed</button>
          <button type="button" onClick={() => { setActionError(""); setNotice(""); setReconcileOpen(true); }} disabled={loading || Boolean(busy)} className="inline-flex min-h-11 items-center rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60">Reconcile imports</button>
        </div>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search export label or reference" total={total} loading={loading} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Status<select aria-label="Filter import status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="pending">Pending</option><option value="active">Active</option><option value="withdrawn">Withdrawn</option><option value="failed">Failed</option></select></label>
      </AdminListToolbar>

      <div className="mb-6 rounded-xl border border-teal-200 bg-teal-50 p-4 text-sm leading-6 text-teal-950 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-100">
        <div className="flex gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" /><p><strong>Consent is required for every import.</strong> Confirm that DI Voices holds explicit participant consent for persona use. Imported personas can be made available to customers for evidence-grounded conversations, while source answers remain within provider controls.</p></div>
      </div>

      {loading && <p role="status">Loading the DI Voices feed…</p>}
      {busy && <p className="mb-4" role="status" aria-live="polite">{busy === "reconcile" ? "Reconciling the authoritative feed…" : "Preparing and importing the selected export. This can take up to two minutes…"}</p>}
      {error && <p className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">{error}</p>}
      {notice && <p className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100" role="status"><CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />{notice}</p>}

      {!loading && !error && (
        <div className="grid gap-4 lg:grid-cols-2">
          {data.exports.map((item) => {
            const imported = item.import || importsById.get(item.id);
            const blocked = imported?.status === "withdrawn";
            const action = imported?.status === "active" ? "Reimport" : imported?.status === "failed" || imported?.status === "pending" ? "Retry import" : "Import personas";
            return (
              <article key={item.id} className="rounded-2xl border border-border bg-background p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{item.scope} export</p><h2 className="mt-1 break-all font-mono text-sm font-semibold">{item.id}</h2></div><StatusBadge status={imported?.status} /></div>
                <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div><dt className="text-muted-foreground">Participants</dt><dd className="mt-1 flex items-center gap-1 font-semibold"><Users className="h-4 w-4" aria-hidden="true" />{item.summary?.participants ?? 0}</dd></div>
                  <div><dt className="text-muted-foreground">Answers</dt><dd className="mt-1 font-semibold">{item.summary?.answers ?? 0}</dd></div>
                  <div><dt className="text-muted-foreground">Original words</dt><dd className="mt-1 font-semibold">{item.summary?.words ?? 0}</dd></div>
                  <div><dt className="text-muted-foreground">Bundle</dt><dd className="mt-1 font-semibold">{byteLabel(item.bytes)}</dd></div>
                </dl>
                <p className="mt-4 text-xs text-muted-foreground">Published {dateLabel(item.created_at)}{imported?.imported_at ? ` · Last imported ${dateLabel(imported.imported_at)}` : ""}</p>
                <button type="button" disabled={blocked || Boolean(busy)} onClick={() => openImport(item)} className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60">{blocked ? "Withdrawn permanently" : action}</button>
              </article>
            );
          })}
          {!data.exports.length && <p className="rounded-xl border border-border bg-muted/40 p-5 text-sm text-muted-foreground">The configured feed currently contains no available exports.</p>}
        </div>
      )}

      {(
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-background p-5 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" role="region" tabIndex={0} aria-label="DI Voices import history">
          <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-xl font-semibold">Import history</h2><label className="text-sm font-medium">Search<input aria-label="Search import history" value={query.history_q || ""} onChange={(event) => update({ history_q: event.target.value, history_offset: "" })} className="ml-2 min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label><label className="text-sm font-medium">Status<select aria-label="Filter import history status" value={query.history_status || "all"} onChange={(event) => update({ history_status: event.target.value, history_offset: "" })} className="ml-2 min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="pending">Pending</option><option value="active">Active</option><option value="withdrawn">Withdrawn</option><option value="failed">Failed</option></select></label><label className="text-sm font-medium">From (UTC)<input aria-label="Filter import history from date" type="datetime-local" value={query.history_from || ""} onChange={(event) => update({ history_from: event.target.value, history_offset: "" })} className="ml-2 min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label><label className="text-sm font-medium">To (UTC)<input aria-label="Filter import history to date" type="datetime-local" value={query.history_to || ""} onChange={(event) => update({ history_to: event.target.value, history_offset: "" })} className="ml-2 min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label></div>
          {historyError && <p className="mt-3 text-sm text-destructive" role="alert">{historyError}</p>}
          {!historyError && !history.items.length && <p className="mt-3 text-sm text-muted-foreground">No import history matches these filters.</p>}
          <table className="mt-3 w-full min-w-[42rem] text-sm"><caption className="sr-only">DI Voices import history</caption><thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-2">Export</th><th>Status</th><th>Personas</th><th>Last seen</th><th>Consent confirmed</th></tr></thead><tbody>{history.items.map((row) => <tr key={row.id} className="border-t border-border"><td className="py-3 font-mono text-xs">{row.export_id}</td><td><StatusBadge status={row.status} /></td><td>{row.participant_count}</td><td>{dateLabel(row.last_seen_at)}</td><td>{dateLabel(row.consent_confirmed_at)}</td></tr>)}</tbody></table>
          <AdminListPager offset={Number(query.history_offset || 0)} limit={25} total={history.total} disabled={Boolean(busy)} onPage={(offset) => update({ history_offset: offset }, { resetOffset: false })} />
        </div>
      )}
      {!loading && <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} disabled={Boolean(busy)} onPage={(offset) => update({ offset }, { resetOffset: false })} />}

      <AlertDialog open={Boolean(selected)} onOpenChange={(open) => { if (!open && !busy) { setSelected(null); setConsent(false); } }}>
        <AlertDialogContent className="admin-console-dialog">
          <AlertDialogHeader><AlertDialogTitle>Confirm consent and import</AlertDialogTitle><AlertDialogDescription>This creates or updates personas from the complete selected export. Active personas can answer customer questions using participant evidence. Manually edited persona names are preserved.</AlertDialogDescription></AlertDialogHeader>
          <form onSubmit={submitImport}>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-sm leading-6"><input type="checkbox" checked={consent} disabled={Boolean(busy)} onChange={(event) => setConsent(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 disabled:cursor-not-allowed" /><span>I confirm that DI Voices recorded explicit consent from every participant in this export for their answers to be used to create personas.</span></label>
            {actionError && <p className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">{actionError}</p>}
            <AlertDialogFooter className="mt-5"><AlertDialogCancel type="button" disabled={Boolean(busy)} className="min-h-11">Cancel</AlertDialogCancel><button type="submit" disabled={!consent || Boolean(busy)} className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50">{busy ? "Importing…" : "Confirm and import"}</button></AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reconcileOpen} onOpenChange={(open) => !busy && setReconcileOpen(open)}>
        <AlertDialogContent className="admin-console-dialog"><AlertDialogHeader><AlertDialogTitle>Reconcile with DI Voices?</AlertDialogTitle><AlertDialogDescription>The server will read the complete authoritative feed. Imports that are no longer listed will be permanently withdrawn, their personas retired, and their retrieval chunks removed.</AlertDialogDescription></AlertDialogHeader>{actionError && <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">{actionError}</p>}<AlertDialogFooter><AlertDialogCancel disabled={Boolean(busy)} className="min-h-11">Cancel</AlertDialogCancel><AlertDialogAction disabled={Boolean(busy)} className="min-h-11" onClick={(event) => { event.preventDefault(); void reconcile(); }}>{busy === "reconcile" ? "Reconciling…" : "Reconcile now"}</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
